import type { IntlShape } from 'react-intl'
import { SAVE_THOUSANDS_REGIONS } from 'luxPlus/constants/benefits'

/**
 * @param regionCode - current region code
 * @param intl - react-intl IntlShape for localisation
 * @returns save amount copy - depending on if the region has a high denomination currency such as ZAR, THB, etc.
 */
function getRegionSaveAmountCopy(regionCode: string, intl: IntlShape) {
  const isSaveThousandsRegion = SAVE_THOUSANDS_REGIONS.includes(regionCode)
  return isSaveThousandsRegion
    ? intl.formatMessage({
        id: 'pHMqUvu-GNMdaMhAT8Go',
        defaultMessage: 'thousands',
      })
    : intl.formatMessage({
        id: 'z1-tI-1zLx8QI1yE2yFF',
        defaultMessage: 'hundreds',
      })
}

/**
 * Approximate equivalent of $450 AUD in each region's local currency.
 * Rounded to marketing-friendly numbers.
 */
const REGION_SAVE_AMOUNTS: Record<string, number> = {
  AU: 836,
  NZ: 910,
  US: 530,
  GB: 420,
  CA: 740,
  SG: 720,
  HK: 4180,
  IN: 44500,
  ZA: 10000,
  TH: 18000,
  KR: 769000,
  ID: 8500000,
  JP: 53800, // see note
  TW: 17600,
  PH: 30600,
  VN: 13900000,
  CN: 3840,
  MY: 2480,
  AE: 1980,
  QA: 1970,
  SA: 2030,
  IL: 1980,
  MO: 4380,
  FR: 500,
  DE: 500,
  IE: 500,
  IT: 500,
  NL: 500,
  ES: 500,
}

const DEFAULT_SAVE_AMOUNT = 836

/**
 * @param regionCode - current region code
 * @returns the approximate save amount in the region's local currency (based on ~$450 AUD)
 */
export function getRegionSaveAmount(regionCode: string): number {
  return REGION_SAVE_AMOUNTS[regionCode] ?? DEFAULT_SAVE_AMOUNT
}

const AUD_BASE = 450

/**
 * Converts an AUD amount to the approximate local currency equivalent
 * using the ratio derived from the REGION_SAVE_AMOUNTS mapping.
 */
export function convertAudToRegion(audAmount: number, regionCode: string): number {
  const localEquivalent = REGION_SAVE_AMOUNTS[regionCode] ?? DEFAULT_SAVE_AMOUNT
  return Math.round(audAmount * (localEquivalent / AUD_BASE))
}

export default getRegionSaveAmountCopy
